import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Button, message } from 'antd';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropModalProps {
  visible: boolean;
  imageFile: File;
  onCancel: () => void;
  onConfirm: (croppedImageBase64: string) => void;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  visible,
  imageFile,
  onCancel,
  onConfirm
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: 'px',
    x: 0,
    y: 0,
    width: 240,
    height: 120
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [messageApi, contextHolder] = message.useMessage();

  // Clamp crop size to min/max in pixels
  const clampCrop = useCallback((c: Crop): Crop => {
    const MIN_SIZE = 30;
    const MAX_SIZE = 300;

    const clamp = (value: number | undefined, min: number, max: number) => {
      const v = typeof value === 'number' ? value : min;
      return Math.max(min, Math.min(max, v));
    };

    return {
      ...c,
      // keep unit as-is, but ensure sizes are within 30–300 px
      width: clamp(c.width as number, MIN_SIZE, MAX_SIZE),
      height: clamp(c.height as number, MIN_SIZE, MAX_SIZE)
    };
  }, []);

  // Additionally clamp x/y so the crop stays within rendered image bounds
  const clampCropBounds = useCallback((c: Crop): Crop => {
    const base = clampCrop(c);
    const imageW = imgRef.current?.width;
    const imageH = imgRef.current?.height;

    if (typeof imageW === 'number' && typeof imageH === 'number') {
      const maxX = Math.max(0, imageW - (base.width as number));
      const maxY = Math.max(0, imageH - (base.height as number));
      const x = Math.max(0, Math.min((base.x ?? 0) as number, maxX));
      const y = Math.max(0, Math.min((base.y ?? 0) as number, maxY));
      return { ...base, x, y };
    }

    return base;
  }, [clampCrop]);

  // Load image when modal opens
  useEffect(() => {
    if (visible && imageFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(imageFile);
    } else {
      setImageSrc('');
    }
  }, [visible, imageFile]);

  // Handle image load to set initial crop
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const displayedWidth = e.currentTarget.width;
    const displayedHeight = e.currentTarget.height;

    // Desired default crop size: 120x240 (width x height)
    const desiredW = 240;
    const desiredH = 120;

    // Scale down if the image is smaller than the desired crop
    const scale = Math.min(1, displayedWidth / desiredW, displayedHeight / desiredH);
    const width = Math.floor(desiredW * scale);
    const height = Math.floor(desiredH * scale);

    setCrop({
      unit: 'px',
      x: Math.max(0, (displayedWidth - width) / 2),
      y: Math.max(0, (displayedHeight - height) / 2),
      width,
      height
    });
  }, []);

  // Generate cropped image
  const getCroppedImg = useCallback(async (image: HTMLImageElement, crop: PixelCrop): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = crop.width;
    canvas.height = crop.height;
    
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas is empty');
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      }, 'image/png');
    });
  }, []);

  // Handle confirm crop
  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop) {
      messageApi.error('Please select a crop area');
      return;
    }
    
    try {
      const croppedImageBase64 = await getCroppedImg(imgRef.current, completedCrop);
      onConfirm(croppedImageBase64);
    } catch (error) {
      messageApi.error('Failed to crop image');
      console.error('Crop error:', error);
    }
  };



  return (
    <>
      {contextHolder}
      <Modal
        title="Crop Image"
        open={visible}
        onCancel={onCancel}
        width={700}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirm}>
            Confirm
          </Button>
        ]}
      >
        {imageSrc && (
          <div style={{ textAlign: 'center' }}>
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(clampCropBounds(newCrop))}
              onComplete={(c) => setCompletedCrop(c)}
              minWidth={30}
              minHeight={30}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={imageSrc}
                style={{ maxHeight: '400px', maxWidth: '100%' }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ImageCropModal;